/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { ScoringDataApi } from './ScoringDataApi';
import { ConfigApi, FetchApi } from '@backstage/core-plugin-api';
import {
  EntityScore,
  EntityScoreExtended,
  EntityScorePillar,
  PortfolioHistories,
  PortfolioHistoryResponse,
  ScoreHistoryResponse,
  ScoreSnapshot,
  SlimScoreSnapshot,
  SystemScoreHistory,
} from './types';
import { CatalogApi } from '@backstage/plugin-catalog-react';
import {
  Entity,
  CompoundEntityRef,
  parseEntityRef,
  RELATION_OWNED_BY,
  DEFAULT_NAMESPACE,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { ScmAuthApi } from '@backstage/integration-react';
import {
  ScmIntegrationRegistry,
  GithubIntegration,
  getGithubFileFetchUrl,
} from '@backstage/integration';
import { percentToScoreSuccess } from '../helpers/percentToScoreSuccess';

/**
 * Combines a primary score's pillars with a secondary source's, pillar-by-pillar,
 * keeping whichever side actually measured that pillar (radar-backend-scorecard:
 * IMC measures engineering/security, radar measures product/delivery/production —
 * each side's own placeholder for the pillars it doesn't own already arrives as
 * `measured: false`, so "prefer the measured one" is enough without hardcoding
 * which source owns which pillar key).
 *
 * Walks the UNION of both sides' pillar keys, primary's order first — not just
 * primary's own list — so a pillar that exists only on the secondary side (e.g.
 * radar's 'production', which IMC's fixed pillar set doesn't declare at all)
 * still surfaces instead of being silently dropped.
 *
 * The overall score/success is then recomputed from the merged pillars — neither
 * source's own overall reflects the other's contribution.
 *
 * Exported for unit testing in isolation from the HTTP/annotation plumbing.
 */
export function mergeEntityScores(
  primary: EntityScore,
  secondary: EntityScore | undefined,
): EntityScore {
  if (!secondary) {
    return primary;
  }

  const secondaryByKey = new Map(secondary.pillars.map(p => [p.key, p]));
  const primaryKeys = new Set(primary.pillars.map(p => p.key));
  const merged: EntityScorePillar[] = primary.pillars.map(pillar => {
    const other = secondaryByKey.get(pillar.key);
    return other?.measured && !pillar.measured ? other : pillar;
  });
  const secondaryOnly = secondary.pillars.filter(p => !primaryKeys.has(p.key));
  const pillars: EntityScorePillar[] = [...merged, ...secondaryOnly];

  const measured = pillars.filter(
    p => p.measured && typeof p.scorePercent === 'number',
  );
  const scorePercent = measured.length
    ? Math.round(
        measured.reduce((sum, p) => sum + (p.scorePercent ?? 0), 0) /
          measured.length,
      )
    : null;

  return {
    ...primary,
    pillars,
    scorePercent,
    scoreSuccess: percentToScoreSuccess(scorePercent) ?? null,
  };
}

// How far back a history merge looks for the other source's snapshot: IMC
// snapshots weekly, radar nightly, so a week (plus a day of slack) pairs them
// without reaching back to stale data.
const HISTORY_MERGE_WINDOW_MS = 8 * 24 * 60 * 60 * 1000;

/** The latest snapshot at or before `time`, within HISTORY_MERGE_WINDOW_MS. */
function snapshotAt<S extends SlimScoreSnapshot>(
  snapshots: S[],
  time: number,
): S | undefined {
  let found: S | undefined;
  for (const snapshot of snapshots) {
    const t = Date.parse(snapshot.time);
    if (t <= time && time - t <= HISTORY_MERGE_WINDOW_MS) found = snapshot;
  }
  return found;
}

/** A snapshot that may carry the Score tab's areas/metrics (absent on a slim one). */
type MaybeFullSnapshot = SlimScoreSnapshot & Partial<ScoreSnapshot>;

/**
 * Merges radar-backend's score history (`secondary`) into IMC's (`primary`),
 * the same way mergeEntityScores merges current scores: every point in time
 * from either source becomes a snapshot carrying both sources' pillars — the
 * other source's from its latest snapshot in the week before — and a Total
 * recomputed as the mean of the measured pillars.
 *
 * Generic over the history shape so the board's slim per-System histories
 * (feature 0056, no areas/metrics) merge by the very same rule as the Score
 * tab's full ones. A merged slim snapshot carries empty area/metric lists.
 */
export function mergeScoreHistories<
  H extends { snapshots: SlimScoreSnapshot[] },
>(primary: H | undefined, secondary: H | undefined): H | undefined {
  if (!secondary?.snapshots.length) return primary;
  if (!primary?.snapshots.length) return secondary;

  const byTime = (a: SlimScoreSnapshot, b: SlimScoreSnapshot) =>
    Date.parse(a.time) - Date.parse(b.time);
  const primarySnapshots: MaybeFullSnapshot[] = [...primary.snapshots].sort(
    byTime,
  );
  const secondarySnapshots: MaybeFullSnapshot[] = [...secondary.snapshots].sort(
    byTime,
  );
  const times = [
    ...new Set(
      [...primarySnapshots, ...secondarySnapshots].map(s => Date.parse(s.time)),
    ),
  ].sort((a, b) => a - b);

  const snapshots = times.map(time => {
    const p = snapshotAt(primarySnapshots, time);
    const s = snapshotAt(secondarySnapshots, time);
    // IMC's pillars first; a radar pillar fills one IMC didn't measure.
    const pillarScores = [...(p?.pillarScores ?? [])];
    for (const pillar of s?.pillarScores ?? []) {
      if (typeof pillar.scorePercent !== 'number') continue;
      const index = pillarScores.findIndex(x => x.key === pillar.key);
      if (index === -1) pillarScores.push(pillar);
      else if (typeof pillarScores[index].scorePercent !== 'number') {
        pillarScores[index] = pillar;
      }
    }
    const scores = pillarScores
      .map(x => x.scorePercent)
      .filter((v): v is number => typeof v === 'number');
    const scorePercent = scores.length
      ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
      : null;
    const areaIds = new Set((p?.areaScores ?? []).map(a => a.id));
    const metricIds = new Set((p?.metricValues ?? []).map(m => m.id));
    const merged: ScoreSnapshot = {
      time: new Date(time).toISOString(),
      scorePercent,
      scoreSuccess: percentToScoreSuccess(scorePercent) ?? null,
      pillarScores,
      areaScores: [
        ...(p?.areaScores ?? []),
        ...(s?.areaScores ?? []).filter(a => !areaIds.has(a.id)),
      ],
      metricValues: [
        ...(p?.metricValues ?? []),
        ...(s?.metricValues ?? []).filter(m => !metricIds.has(m.id)),
      ],
    };
    return merged;
  });

  return { ...primary, snapshots } as H;
}

/**
 * Default JSON data client. Expects JSON files in a format see /sample-data
 */
export class ScoringDataJsonClient implements ScoringDataApi {
  configApi: ConfigApi;
  catalogApi: CatalogApi;
  fetchApi: FetchApi;
  scmAuthApi: ScmAuthApi;
  scmIntegrationsApi: ScmIntegrationRegistry;

  constructor({
    configApi,
    catalogApi,
    fetchApi,
    scmAuthApi,
    scmIntegrationsApi,
  }: {
    configApi: ConfigApi;
    catalogApi: CatalogApi;
    fetchApi: FetchApi;
    scmAuthApi: ScmAuthApi;
    scmIntegrationsApi: ScmIntegrationRegistry;
  }) {
    this.configApi = configApi;
    this.catalogApi = catalogApi;
    this.fetchApi = fetchApi;
    this.scmAuthApi = scmAuthApi;
    this.scmIntegrationsApi = scmIntegrationsApi;
  }

  public async getScore(
    entity?: Entity,
  ): Promise<EntityScoreExtended | undefined> {
    if (!entity) {
      return undefined;
    }
    const jsonFromAnnotation = this.getAnnotationValue(
      entity,
      'scorecard/jsonDataUrl',
    );

    let urlWithData: string;

    if (jsonFromAnnotation) {
      urlWithData = jsonFromAnnotation;
    } else {
      const jsonDataUrl = this.getJsonDataUrl();
      urlWithData = `${jsonDataUrl}${
        entity.metadata.namespace ?? DEFAULT_NAMESPACE
      }/${entity.kind}/${entity.metadata.name}.json`.toLowerCase();
    }

    this.logConsole(
      `ScoringDataJsonClient: fetching score from: ${urlWithData}`,
    );
    const result: EntityScore | undefined = await this.getResult<EntityScore>(
      urlWithData,
    );

    // An explicit per-entity annotation is a full override of where this
    // entity's data comes from — it opts out of the radar merge too, same as
    // it already opts out of the global jsonDataUrl.
    if (jsonFromAnnotation) {
      return result ? this.extendEntityScore(result, [entity]) : undefined;
    }
    // A System IMC doesn't score (or a radar project with no System at all)
    // still has radar's own score.
    const radar = await this.getRadarScore(entity);
    const merged = result ? mergeEntityScores(result, radar) : radar;
    return merged ? this.extendEntityScore(merged, [entity]) : undefined;
  }

  public async getRadarPillar(
    entity: Entity,
    pillarKey: string,
    boards: string[],
  ): Promise<EntityScorePillar | undefined> {
    const score = await this.getRadarScore(entity, boards);
    return score?.pillars.find(p => p.key === pillarKey);
  }

  /**
   * Fetches this entity's radar-backend score, if `scorecards.radarJsonDataUrl`
   * is configured — with `boards`, Delivery covers only those Jira boards.
   */
  private async getRadarScore(
    entity: Entity,
    boards: string[] = [],
  ): Promise<EntityScore | undefined> {
    const radarJsonDataUrl = this.getRadarJsonDataUrl();
    if (!radarJsonDataUrl) {
      return undefined;
    }
    const namespace = (
      entity.metadata.namespace ?? DEFAULT_NAMESPACE
    ).toLowerCase();
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name.toLowerCase();
    const query = boards.length
      ? `?${boards
          .map(board => `board=${encodeURIComponent(board)}`)
          .join('&')}`
      : '';
    return this.getResult<EntityScore>(
      `${radarJsonDataUrl}${namespace}/${kind}/${name}.json${query}`,
    );
  }

  public async getAllScores(
    entityKindFilter?: string[],
    entity?: Entity,
  ): Promise<EntityScoreExtended[] | undefined> {
    const jsonFromAnnotation = entity
      ? this.getAnnotationValue(entity, 'scorecard/jsonDataUrl')
      : undefined;

    let urlWithData;
    if (jsonFromAnnotation) {
      urlWithData = jsonFromAnnotation;
    } else {
      const jsonDataUrl = this.getJsonDataUrl();
      urlWithData = `${jsonDataUrl}all.json`;
    }

    this.logConsole(
      `ScoringDataJsonClient: fetching all scores from ${urlWithData}`,
    );
    let result: EntityScore[] | undefined = await this.getResult<EntityScore[]>(
      urlWithData,
    );

    if (!result) return undefined;

    // Same annotation-opts-out-of-the-merge rule as getScore.
    if (!jsonFromAnnotation) {
      result = await this.mergeAllWithRadar(result);
    }

    // Filter entities by kind
    if (entityKindFilter && entityKindFilter.length) {
      result = result.filter(e =>
        entityKindFilter
          .map(f => f.toLocaleLowerCase())
          .includes(e.entityRef?.kind.toLowerCase() as string),
      );
    }

    const entity_names: Set<string> = result.reduce((acc, a) => {
      if (a.entityRef?.name) {
        acc.add(a.entityRef.name);
      }
      return acc;
    }, new Set<string>());

    const fetchAllEntities =
      this.configApi.getOptionalBoolean('scorecards.fetchAllEntities') ?? false;
    const response = await this.catalogApi.getEntities({
      filter: fetchAllEntities
        ? undefined
        : {
            'metadata.name': Array.from(entity_names),
          },
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'spec.owner',
        // The board's Domain scope filters on spec.domain (feature 0039).
        'spec.domain',
        'relations',
      ],
    });
    const entities: Entity[] = fetchAllEntities
      ? response.items.filter(i => entity_names.has(i.metadata.name))
      : response.items;

    return result.map<EntityScoreExtended>((score: EntityScore) => {
      return this.extendEntityScore(score, entities);
    });
  }

  public async getScoreHistory(
    entity?: Entity,
    from?: string,
    to?: string,
  ): Promise<ScoreHistoryResponse | undefined> {
    if (!entity) {
      return undefined;
    }

    const jsonFromAnnotation = this.getAnnotationValue(
      entity,
      'scorecard/jsonDataUrl',
    );

    let baseUrl: string;
    if (jsonFromAnnotation) {
      // Annotation points to entity score file (e.g. .../bitcore.json)
      // Derive history path by replacing .json with /history.json
      baseUrl = jsonFromAnnotation.replace(/\.json$/i, '/history.json');
    } else {
      const jsonDataUrl = this.getJsonDataUrl();
      const name = entity.metadata.name.toLowerCase();
      const namespace = (
        entity.metadata.namespace ?? DEFAULT_NAMESPACE
      ).toLowerCase();
      const kind = entity.kind.toLowerCase();
      baseUrl = `${jsonDataUrl}${namespace}/${kind}/${name}/history.json`;
    }

    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const query = params.toString() ? `?${params.toString()}` : '';
    const urlWithData = `${baseUrl}${query}`;

    this.logConsole(
      `ScoringDataJsonClient: fetching score history from: ${urlWithData}`,
    );
    const history = await this.getResult<ScoreHistoryResponse>(urlWithData);
    // Same annotation-opts-out-of-the-merge rule as getScore.
    return jsonFromAnnotation
      ? history
      : mergeScoreHistories(history, await this.getRadarHistory(entity, query));
  }

  /** Fetches this entity's radar-backend score history, if `scorecards.radarJsonDataUrl` is configured. */
  private async getRadarHistory(
    entity: Entity,
    query: string,
  ): Promise<ScoreHistoryResponse | undefined> {
    const radarJsonDataUrl = this.getRadarJsonDataUrl();
    if (!radarJsonDataUrl) {
      return undefined;
    }
    const namespace = (
      entity.metadata.namespace ?? DEFAULT_NAMESPACE
    ).toLowerCase();
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name.toLowerCase();
    return this.getResult<ScoreHistoryResponse>(
      `${radarJsonDataUrl}${namespace}/${kind}/${name}/history.json${query}`,
    );
  }

  /**
   * Every System's score history over `from`..`to` (YYYY-MM-DD) for the
   * board's portfolio trend (feature 0056): IMC's and radar-backend's bulk
   * routes fetched in parallel and merged per System by mergeScoreHistories,
   * keyed by lower-cased `entityRef.name`. A radar-only project contributes
   * radar alone. Radar unconfigured, 404 or failing → IMC alone, as on the
   * Score tab. IMC 404 or failing throws, so the card can say so inline
   * without the rest of the board noticing.
   */
  public async getPortfolioHistory(
    from: string,
    to: string,
  ): Promise<PortfolioHistories> {
    const query = `?${new URLSearchParams({ from, to }).toString()}`;
    const radarJsonDataUrl = this.getRadarJsonDataUrl();
    const [imc, radar] = await Promise.all([
      this.getResult<PortfolioHistoryResponse>(
        `${this.getJsonDataUrl()}history/all.json${query}`,
      ),
      radarJsonDataUrl
        ? this.getResult<PortfolioHistoryResponse>(
            `${radarJsonDataUrl}history/all.json${query}`,
          ).catch(() => undefined)
        : Promise.resolve(undefined),
    ]);
    if (!Array.isArray(imc?.systems)) {
      throw new Error('Portfolio score history is not available');
    }

    const byName = (response: PortfolioHistoryResponse | undefined) => {
      const map = new Map<string, SystemScoreHistory>();
      for (const system of Array.isArray(response?.systems)
        ? response!.systems
        : []) {
        // A malformed entry is skipped, not thrown on: a throw here would
        // lose the whole trend (IMC) or bypass the IMC-alone fallback (radar).
        const rawName = system?.entityRef?.name;
        if (typeof rawName !== 'string') continue;
        const name = rawName.toLowerCase();
        if (name && Array.isArray(system.snapshots)) map.set(name, system);
      }
      return map;
    };
    const imcByName = byName(imc);
    const radarByName = byName(radar);

    const histories: PortfolioHistories = {};
    for (const name of new Set([...imcByName.keys(), ...radarByName.keys()])) {
      const snapshots = mergeScoreHistories(
        imcByName.get(name),
        radarByName.get(name),
      )?.snapshots;
      if (snapshots?.length) {
        histories[name] = [...snapshots].sort(
          (a, b) => Date.parse(a.time) - Date.parse(b.time),
        );
      }
    }
    return histories;
  }

  // ---- HELPER METHODS ---- //

  private logConsole(_: string) {
    // eslint-disable-next-line no-console
    // console.log(msg);
  }

  private async getResult<T>(jsonDataUrl: string): Promise<T | undefined> {
    // Find out if we have an configured integration for the given URL
    const integration = this.scmIntegrationsApi.byUrl(jsonDataUrl);

    // Find any configured authentication for the URL
    let auth;
    try {
      auth = await this.scmAuthApi.getCredentials({ url: jsonDataUrl });
    } catch (error) {
      this.logConsole(
        `No authentication config found for ${jsonDataUrl}, proceeding without authentication`,
      );
    }

    // perform any custom config for the integration type
    let requestUrl;
    let headers = {};
    switch (integration?.type) {
      case 'github':
        requestUrl = getGithubFileFetchUrl(
          jsonDataUrl,
          (integration as GithubIntegration).config,
          { ...auth, type: 'token' },
        );
        headers = {
          ...(auth && auth.headers),
          Accept: 'application/vnd.github.v3.raw',
        };
        break;
      default:
        requestUrl = jsonDataUrl;
        break;
    }

    try {
      const result = await this.fetchApi.fetch(requestUrl, {
        headers,
      });

      if (result.status === 404) {
        return undefined;
      } else if (result.status !== 200) {
        return undefined;
      }
      const json = (await result.json()) as T;
      this.logConsole(`result: ${JSON.stringify(json)}`);
      return json;
    } catch (error) {
      const status = (error as { status?: unknown })?.status;
      throw new Error(`error from server (code ${status})`);
    }
  }

  private getJsonDataUrl() {
    return (
      this.configApi.getOptionalString('scorecards.jsonDataUrl') ??
      'https://unknown-url-please-configure/'
    );
  }

  /**
   * `scorecards.radarJsonDataUrl` (radar-backend-scorecard) — absent by
   * default, so environments/tests that don't set it behave exactly as before
   * this merge existed.
   */
  private getRadarJsonDataUrl() {
    return this.configApi.getOptionalString('scorecards.radarJsonDataUrl');
  }

  /**
   * Merges radar-backend's board (`all.json`) into `primary` (IMC's board),
   * keyed by entityRef. A radar project with no IMC row is added as a row of
   * its own: teams whose work has no catalog System yet (or one IMC doesn't
   * score) still get Product/Delivery, with Engineering/Security empty. Such a
   * row is named by radar's `title` and opens on the board, not the catalog.
   */
  private async mergeAllWithRadar(
    primary: EntityScore[],
  ): Promise<EntityScore[]> {
    const radarJsonDataUrl = this.getRadarJsonDataUrl();
    if (!radarJsonDataUrl) {
      return primary;
    }
    const secondary = await this.getResult<EntityScore[]>(
      `${radarJsonDataUrl}all.json`,
    );
    if (!secondary) {
      return primary;
    }
    const refKey = (ref: CompoundEntityRef) =>
      stringifyEntityRef({
        kind: ref.kind,
        namespace: ref.namespace ?? DEFAULT_NAMESPACE,
        name: ref.name,
      }).toLowerCase();
    const secondaryByKey = new Map(
      secondary.map(score => [refKey(score.entityRef), score]),
    );
    const primaryKeys = new Set(primary.map(score => refKey(score.entityRef)));
    return [
      ...primary.map(score =>
        mergeEntityScores(score, secondaryByKey.get(refKey(score.entityRef))),
      ),
      ...secondary.filter(score => !primaryKeys.has(refKey(score.entityRef))),
    ];
  }

  private extendEntityScore(
    score: EntityScore,
    entities: Entity[] | undefined,
  ): EntityScoreExtended {
    if (score === null) {
      throw new Error(`can not extend null entity score.`);
    }
    if (typeof score === 'undefined') {
      throw new Error(`can not extend undefined entity score.`);
    }

    // Match by FULL entity ref (kind + namespace + name), not name alone: the
    // catalog fetch pulls entities of every kind that share a name, so a System
    // can be shadowed by a like-named owner Group (e.g. system:default/prm vs
    // group:default/prm). A name-only match could bind the score to the Group,
    // whose metadata.title is undefined, and the display would fall back to the
    // raw lowercase ref name. stringifyEntityRef already normalises case.
    const scoreRef = stringifyEntityRef(score.entityRef).toLowerCase();
    const catalogEntity = entities
      ? entities.find(
          entity => stringifyEntityRef(entity).toLowerCase() === scoreRef,
        )
      : undefined;

    const owner = catalogEntity?.relations?.find(
      r => r.type === RELATION_OWNED_BY,
    )?.targetRef;

    let reviewer = undefined;
    if (
      score.scoringReviewer &&
      !(score.scoringReviewer as CompoundEntityRef)?.name
    ) {
      reviewer = {
        name: score.scoringReviewer as string,
        kind: 'User',
        namespace: 'default',
      };
    } else if ((score.scoringReviewer as CompoundEntityRef)?.name) {
      const scoringReviewer = score.scoringReviewer as CompoundEntityRef;
      reviewer = {
        name: scoringReviewer.name,
        kind: scoringReviewer?.kind ?? 'User',
        namespace: scoringReviewer?.namespace ?? DEFAULT_NAMESPACE,
      };
    }

    const reviewDate = score.scoringReviewDate
      ? new Date(score.scoringReviewDate)
      : undefined;
    // Spread `score` FIRST so the derived fields below win. The catalog display
    // title must not be clobbered by any `title` that might appear on the IMC
    // payload, and `id`/`owner`/`reviewer`/`reviewDate` are always the resolved
    // values, not whatever the raw score carried.
    return {
      ...score,
      id: stringifyEntityRef(score.entityRef),
      owner: owner ? parseEntityRef(owner) : undefined,
      reviewer: reviewer,
      reviewDate: reviewDate,
      // A row without a catalog System keeps radar's own title.
      title: catalogEntity?.metadata?.title ?? score.title,
      ...(entities && !catalogEntity ? { inCatalog: false } : {}),
      // Spread in, so a System that declares no domain carries no `domain` key
      // at all rather than one holding undefined — the score is simply not part
      // of any Domain scope (feature 0039).
      ...(typeof catalogEntity?.spec?.domain === 'string'
        ? { domain: catalogEntity.spec.domain }
        : {}),
    };
  }

  private getAnnotationValue(entity: Entity, annotation: string) {
    if (!entity.metadata.annotations) {
      return undefined;
    }

    if (annotation && !entity.metadata.annotations[annotation]) {
      return undefined;
    }
    return entity.metadata.annotations[annotation];
  }
}
